const getDataByKey = (searchPath, key, module) => {
    if (module[searchPath]) {
        return module[searchPath]
            .filter((dataByKey) => dataByKey.type === key && dataByKey.content)
            .map((dataByContent) => dataByContent.content);
    }
    return [];
};

/**
 * Load the resource file by creating an element and add to the dom
 *
 * @param {string} url       Resource url
 * @param {Document} dom     Document
 * @param {string} tagName   Tag name
 * @param {object} attrs     Attributes for the tag
 * @returns {Promise}
 */
const loadResource = (url, dom, tagName, attrs = {}) => new Promise(resolve => {
    const eventName = `resource-loaded-${url}`;
    const el = dom.getElementById(url);

    // Element already exist and the resource already loaded
    if (el && el.dataset.loaded) {
        return resolve();
    }

    // Element already exist but the resource is still loading
    if (el) {
        return dom.addEventListener(eventName, resolve, { once: true });
    }

    // Create a new element and add to the document
    const tag = dom.createElement(tagName);

    tag.setAttribute('id', url);

    for (const [name, value] of Object.entries(attrs)) {
        tag.setAttribute(name, value);
    }

    const onLoaded = () => {
        tag.dataset.loaded = true;
        resolve();
        dom.dispatchEvent(new Event(eventName));
    };

    tag.onload = onLoaded;
    tag.onerror = onLoaded;

    dom.head.appendChild(tag);
});

/**
 * Load javascript files
 *
 * @param {string[]} urls
 * @param {Document} dom
 * @returns {Promise}
 */
const loadJsFiles = (urls, dom) => Promise.all(
    urls.map(url =>
        loadResource(url, dom, 'script', {
            type: 'text/javascript',
            async: 'async',
            src: url
        })
    )
);

/**
 * Load css files
 *
 * @param {string[]} urls
 * @param {Document} dom
 * @returns {Promise}
 */
const loadCssFiles = (urls, dom) => Promise.all(
    urls.map(url =>
        loadResource(url, dom, 'link', {
            rel: 'stylesheet',
            type: 'text/css',
            href: url
        })
    )
);

const renderCssInlineAndHTML = (html, cssinline, docId, dom) => new Promise((resolve) => {
    const clientPlacementDoc = dom.getElementById(docId);
    // Create an observer instance linked to the callback function
    const observer = new MutationObserver(() => {
        observer.disconnect();
        resolve();
    });
    if (clientPlacementDoc) {
        observer.observe(clientPlacementDoc, {
            attributes: false,
            childList: true,
            subtree: true,
        });
        clientPlacementDoc.innerHTML = html;
        clientPlacementDoc.style = cssinline;
    } else {
        resolve();
    }
});

const renderInlineScript = (text, dom) => {
    const newScript = dom.createElement('script');
    newScript.type = 'text/javascript';
    newScript.text = text;
    dom.head.appendChild(newScript);
};

const executeHTMLDOCScript = (docId, dom) => {
    const doc = dom.getElementById(docId);
    if (doc) {
        [...doc.getElementsByTagName('script')].forEach((script) => renderInlineScript(script.text, dom));
    }
};

const hideSkeleton = (skeletonId) => {
    const skeletonDoc = document.getElementById(skeletonId);
    if (skeletonDoc) {
        skeletonDoc.style.display = 'none';
    }
};

const preloadJS = (url, dom) => {
    const id = `preload-${url}`;

    if (dom.getElementById(id)) return;

    const tag = dom.createElement('link');
    tag.setAttribute('id', id);
    tag.setAttribute('as', 'script');
    tag.setAttribute('rel', 'preload');
    tag.setAttribute('href', url);
    dom.head.appendChild(tag);
};

const render = async(module, docId, dom, skeletonId) => {
    // Preloads JS files
    const jsURL = getDataByKey('js', 'JS_URL', module);
    jsURL.forEach((url) => {
        preloadJS(url, dom);
    });

    const cssURL = getDataByKey('css', 'CSS_URL', module);
    // All CSS can be called in parallel
    await loadCssFiles(cssURL, dom);

    const cssInline = getDataByKey(
        'css',
        'CSS_INLINE',
        module
    ).reduce((acc, curr) => `${curr} ${acc}`, '').trim();

    const html = (module.html && module.html.content) ? module.html.content : '<div />';
    await renderCssInlineAndHTML(html, cssInline, docId, dom);
    hideSkeleton(skeletonId);

    // All JS can be called in parallel
    await loadJsFiles(jsURL, dom);

    executeHTMLDOCScript(docId, dom);
    getDataByKey('js', 'JS_INLINE', module)
        .forEach((text) => {
            renderInlineScript(text, dom);
        });
    window.dispatchEvent(new Event(`deli-response-rendered-${docId}`));
};

const renderReady = (module) => {
    const loading =
    module && module.loading
        ? module.loading
        : 'IMMEDIATE';
    return new Promise((resolve) => {
        switch (loading) {
            case 'IMMEDIATE':
                return resolve();
            case 'DOCUMENT_READY': {
                if (document.readyState !== 'loading') {
                    return resolve();
                }
                document.addEventListener(
                    'DOMContentLoaded',
                    () => resolve(),
                    { once: true }
                );

                break;
            }
            case 'WINDOW_LOAD': {
                if (document.readyState === 'complete') {
                    return resolve();
                }
                window.addEventListener(
                    'load',
                    () => resolve(),
                    { once: true }
                );

                break;
            }
            case 'WINDOW_LOAD_DELAYED': {
                if (document.readyState === 'complete') {
                    return resolve();
                }
                window.addEventListener(
                    'load',
                    () => resolve(),
                    { once: true }
                );

                break;
            }
            default:
                return resolve();
        }
    });
};

const initGlobalWindowEbayObject = () => {
    if (window.ebay) {
        if (window.ebay.ads) {
            if (!window.ebay.ads.cache) {
                window.ebay.ads.cache = [];
            }
            if (!window.ebay.ads.lazy) {
                window.ebay.ads.lazy = {};
            }
            if (!window.ebay.ads.tracking) {
                window.ebay.ads.tracking = {};
            }
        } else {
            window.ebay.ads = {
                cache: [],
                lazy: [],
                tracking: {},
            };
        }
    } else {
        window.ebay = {
            ads: {
                cache: [],
                lazy: {},
                tracking: {},
            }
        };
    }
};
module.exports = {
    renderReady,
    render,
    hideSkeleton,
    initGlobalWindowEbayObject
};
